import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authApi, userApi, iaApi } from '../services/api';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    setUser(null);
    navigate('/login');
  }, [navigate]);

  const hydrateSession = useCallback(async (authToken) => {
    setToken(authToken);
    localStorage.setItem('token', authToken);
    const response = await authApi.me();
    setUser(response.data);
    try {
      await iaApi.readiness();
      sessionStorage.setItem('iron_ai_ok', '1');
    } catch {
      sessionStorage.removeItem('iron_ai_ok');
    }
    return response.data;
  }, []);

  const loadUser = useCallback(
    async (authToken) => {
      try {
        await hydrateSession(authToken);
        setLoading(false);
      } catch (err) {
        console.error('Failed to load user:', err);
        logout();
        setLoading(false);
      }
    },
    [logout, hydrateSession]
  );

  useEffect(() => {
    const storedToken = localStorage.getItem('token');
    if (storedToken) {
      loadUser(storedToken);
    } else {
      setLoading(false);
    }
  }, [loadUser]);

  const refreshUser = async () => {
    const response = await authApi.me();
    setUser(response.data);
    return response.data;
  };

  const login = async (email, password) => {
    try {
      setLoading(true);
      const normEmail =
        typeof email === 'string' ? email.trim().toLowerCase() : email;
      const response = await authApi.login({ email: normEmail, password });
      return await hydrateSession(response.data.token);
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const register = async (
    name,
    email,
    password,
    level = 'intermediario',
    trainingGoalSlugs = ['hipertrofia']
  ) => {
    try {
      setLoading(true);
      const normEmail =
        typeof email === 'string' ? email.trim().toLowerCase() : email;
      const slugs = Array.isArray(trainingGoalSlugs)
        ? trainingGoalSlugs
        : trainingGoalSlugs
          ? [trainingGoalSlugs]
          : ['hipertrofia'];
      const response = await authApi.register({
        name,
        email: normEmail,
        password,
        level,
        trainingGoalSlugs: slugs,
        goal: slugs[0],
      });
      const { user: registeredUser } = response.data;

      // Auto login after register
      await login(normEmail, password);

      return registeredUser;
    } catch (err) {
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const updateUser = async (data) => {
    try {
      const response = await userApi.update(user.id, data);
      setUser(response.data);
      return response.data;
    } catch (err) {
      throw err;
    }
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    registerPending: async (payload) => {
      const response = await authApi.registerPending(payload);
      return response.data;
    },
    hydrateSession,
    updateUser,
    refreshUser,
    logout,
    isAuthenticated: !!token,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
